import * as React from 'react'
import { ArrowDownLeft, ArrowUpRight, ChevronRight as CaretIcon } from 'lucide-react'
import {
  GRAPH_NODE_TYPE_REGISTRY,
  RELATIONSHIP_PROPERTY_SCHEMAS,
  RELATIONSHIP_TYPES,
  type NodeLabel,
  type RelationshipType
} from '@atbash-labs/ontology-core'
import {
  usePlantSessionPaint,
  type PlantEntityConnectionGroup,
  type PlantEntityConnectionRow
} from '@axilon-apps/domains-ontology-ui-state'
import { SourceDocChip, type SourceDocRef } from '../../../components/SourceDocViewer'
import { Badge } from '../../../components/ui/badge'
import { cn } from '../../../lib/utils'
import { CollapsibleContent } from '../CollapsibleContent'
import { PlantHref } from '../PlantHref'
import {
  SessionCompactDeletedBadge,
  SessionEntityMark,
  SessionOtherSectionBadge,
  SessionRollupPill,
  SessionRowEndMark,
  sessionMarkClass,
  useVisibleGhostRels
} from '../session-mark-ui'
import { SessionInlinePropertyChanges } from '../session-property-changes'
import { ConfidenceValue } from './ConfidenceValue'
import { InspectorHeading } from './InspectorHeading'
import { formatTypedProperty } from './TypedPropertiesSection'

function relationshipType(value: string): RelationshipType | null {
  return (RELATIONSHIP_TYPES as readonly string[]).includes(value)
    ? (value as RelationshipType)
    : null
}

function nodeLabel(labels: string[]): NodeLabel | null {
  return (
    labels.find((label): label is NodeLabel =>
      Object.prototype.hasOwnProperty.call(GRAPH_NODE_TYPE_REGISTRY, label)
    ) ?? null
  )
}

type GhostRelRow = ReturnType<typeof useVisibleGhostRels>[number]

/** Same unit-separator triple as ontology-graph `relId` (avoid a static graph import here). */
const REL_ID_SEP = '\u001f'

function edgeLogicalId(edge: PlantEntityConnectionRow['edge']): string {
  const id = edge.props['id']
  if (typeof id === 'string' && id.length > 0) return id
  return `${edge.source}${REL_ID_SEP}${edge.type}${REL_ID_SEP}${edge.target}`
}

export function connectionChangeCounts(
  rows: readonly PlantEntityConnectionRow[],
  ghosts: readonly Pick<GhostRelRow, 'view'>[],
  relationshipMarkFor: (id: string) => string | undefined
): { added: number; modified: number; deleted: number } {
  let added = 0
  let modified = 0
  for (const row of rows) {
    const mark = relationshipMarkFor(edgeLogicalId(row.edge))
    if (mark === 'added') added += 1
    else if (mark === 'touched') modified += 1
  }
  return {
    added,
    modified,
    deleted: ghosts.filter((ghost) => ghost.view === 'deleted').length
  }
}

/**
 * Compact grouped relationship browser embedded in EntityDetailsInspector.
 */
export function ConnectionsPanel({
  entityId,
  groups,
  isLoading,
  jobId,
  onOpenSource
}: {
  /** Current page entity — used to place session-deleted relationship ghosts. */
  entityId: string
  groups: PlantEntityConnectionGroup[]
  isLoading: boolean
  /** Page entity's last ingest job — edges borrow it for source chips. */
  jobId?: string
  onOpenSource: (args: SourceDocRef) => void
}): React.ReactElement | null {
  const ghostRels = useVisibleGhostRels(entityId)
  const ghostsByGroup = React.useMemo(() => {
    const map = new Map<string, GhostRelRow[]>()
    for (const ghost of ghostRels) {
      const key = `${ghost.type}:${ghost.direction}`
      const list = map.get(key)
      if (list) list.push(ghost)
      else map.set(key, [ghost])
    }
    return map
  }, [ghostRels])

  const displayGroups = React.useMemo(() => {
    const liveKeys = new Set(groups.map((group) => `${group.type}:${group.direction}`))
    const extras: PlantEntityConnectionGroup[] = []
    for (const [key, ghosts] of ghostsByGroup) {
      if (liveKeys.has(key) || ghosts.length === 0) continue
      const ghost = ghosts[0]
      extras.push({
        type: ghost.type,
        direction: ghost.direction,
        total: 0,
        rows: []
      })
    }
    return [...groups, ...extras]
  }, [ghostsByGroup, groups])

  if (isLoading) {
    return <div className="py-2.5 text-12 text-ink-3">Loading connections…</div>
  }
  if (displayGroups.length === 0) return null

  const liveCount = groups.reduce((sum, group) => sum + group.total, 0)

  return (
    <section className="py-2.5">
      <InspectorHeading label="Connections" count={liveCount} />
      <div className="mt-1.5 flex flex-col gap-1.5">
        {displayGroups.map((group) => (
          <ConnectionGroupRow
            key={`${group.type}:${group.direction}`}
            group={group}
            ghosts={ghostsByGroup.get(`${group.type}:${group.direction}`) ?? []}
            jobId={jobId}
            onOpenSource={onOpenSource}
          />
        ))}
      </div>
    </section>
  )
}

function ConnectionGroupRow({
  group,
  ghosts,
  jobId,
  onOpenSource
}: {
  group: PlantEntityConnectionGroup
  ghosts: GhostRelRow[]
  jobId?: string
  onOpenSource: (args: SourceDocRef) => void
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false)
  const { relationshipMarkFor } = usePlantSessionPaint()
  const DirectionIcon = group.direction === 'out' ? ArrowUpRight : ArrowDownLeft
  const relType = relationshipType(group.type)
  const changeCounts = connectionChangeCounts(group.rows, ghosts, relationshipMarkFor)

  return (
    <div className="rounded-md border bg-bg-elev">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-8 w-full items-center gap-1.5 px-2.5 py-1.5 text-left hover:bg-accent"
        aria-expanded={expanded}
      >
        <CaretIcon
          size={12}
          className={cn('shrink-0 text-ink-3 transition-transform', expanded && 'rotate-90')}
          aria-hidden
        />
        <DirectionIcon size={12} className="shrink-0 text-ink-3" aria-hidden />
        <span className="min-w-0 flex-1 truncate font-mono text-12 font-semibold text-ink">
          {group.type}
        </span>
        <Badge variant="outline" className="px-1.5 py-0 text-10">
          {group.direction === 'out' ? 'outgoing' : 'incoming'}
        </Badge>
        <span className="font-mono text-10 tabular-nums text-ink-3">{group.total}</span>
        <SessionRollupPill
          added={changeCounts.added}
          modified={changeCounts.modified}
          deleted={changeCounts.deleted}
        />
      </button>
      <CollapsibleContent open={expanded}>
        <div className="max-h-105 divide-y overflow-y-auto border-t">
          {group.rows.map((row, rowIndex) => (
            <ConnectionPeerRow
              key={`${row.peer.id}:${row.edge.props['id'] ?? group.type}:${rowIndex}`}
              row={row}
              relType={relType}
              jobId={jobId}
              onOpenSource={onOpenSource}
            />
          ))}
          {ghosts.map((ghost) => (
            <ConnectionGhostPeerRow key={ghost.relId} ghost={ghost} />
          ))}
        </div>
      </CollapsibleContent>
    </div>
  )
}

function ConnectionGhostPeerRow({ ghost }: { ghost: GhostRelRow }): React.ReactElement {
  const label = nodeLabel(ghost.peerLabels)
  const dimmed = ghost.view === 'dimmed'
  return (
    <div
      className={cn(
        'flex h-7 cursor-not-allowed items-center gap-1.5 px-2',
        dimmed && 'bg-bg-sunk/70'
      )}
      title={dimmed ? 'changed by another section' : 'deleted this session'}
    >
      <span className="size-4 shrink-0" />
      {label ? (
        <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-9 leading-4">
          {label}
        </Badge>
      ) : null}
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-12 font-medium line-through',
          dimmed ? 'text-ink-4' : 'text-st-down'
        )}
      >
        {ghost.peerName}
      </span>
      {dimmed ? <SessionOtherSectionBadge /> : <SessionCompactDeletedBadge />}
    </div>
  )
}

function ConnectionPeerRow({
  row: { peer, peerLabels, edge },
  relType,
  jobId,
  onOpenSource
}: {
  row: PlantEntityConnectionRow
  relType: RelationshipType | null
  jobId?: string
  onOpenSource: (args: SourceDocRef) => void
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false)
  const { relationshipMarkFor } = usePlantSessionPaint()
  const relId = edgeLogicalId(edge)
  const relationshipMark = relationshipMarkFor(relId)
  const label = nodeLabel(peerLabels)
  const displayField = label ? GRAPH_NODE_TYPE_REGISTRY[label].displayLabelField : 'name'
  const display = typeof peer[displayField] === 'string' ? peer[displayField] : peer.name
  const declared = relType ? RELATIONSHIP_PROPERTY_SCHEMAS[relType].properties : {}
  const orderedProps = [
    ...Object.keys(declared),
    ...Object.keys(edge.props).filter((key) => !(key in declared))
  ].filter((key, index, keys) => keys.indexOf(key) === index && edge.props[key] != null)
  const hasDetails = orderedProps.length > 0

  return (
    <div>
      <div
        className={sessionMarkClass(
          'flex h-7 items-center gap-1.5 border-0 px-2',
          relationshipMark
        )}
      >
        {hasDetails ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-label={expanded ? 'Hide relationship properties' : 'Show relationship properties'}
            aria-expanded={expanded}
            className="grid size-4 shrink-0 place-items-center rounded-sm text-ink-3 hover:bg-accent"
          >
            <CaretIcon
              size={11}
              className={cn('transition-transform', expanded && 'rotate-90')}
              aria-hidden
            />
          </button>
        ) : (
          <span className="size-4 shrink-0" />
        )}
        {label && (
          <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-9 leading-4">
            {label}
          </Badge>
        )}
        <PlantHref
          id={peer.id}
          label={label ?? undefined}
          node={peer}
          title={peer.id}
          className="min-w-0 flex-1 truncate text-12 font-medium text-brand hover:underline"
        >
          {String(display)}
        </PlantHref>
        <SourceDocChip
          compact
          jobId={jobId}
          sourceFile={edge.provenance?.source_file}
          sourcePage={edge.provenance?.source_page}
          sourceExcerpt={edge.provenance?.source_excerpt}
          onOpen={onOpenSource}
        />
        <ConfidenceValue value={edge.confidence} className="whitespace-nowrap" />
        {relationshipMark !== undefined ? (
          <SessionRowEndMark mark={relationshipMark} />
        ) : (
          <SessionEntityMark entityId={peer.id} />
        )}
      </div>
      <div className="px-2">
        <SessionInlinePropertyChanges entityId={relId} />
      </div>
      <CollapsibleContent open={expanded}>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 border-t bg-bg-2/60 px-7 py-1.5 sm:grid-cols-3 xl:grid-cols-[repeat(auto-fill,minmax(9.375rem,1fr))]">
          {orderedProps.map((key) => {
            const schema = declared[key]
            const value = schema
              ? formatTypedProperty(edge.props[key], schema).join(', ')
              : edge.props[key] !== null && typeof edge.props[key] === 'object'
                ? JSON.stringify(edge.props[key])
                : String(edge.props[key])
            const isId = key.toLowerCase() === 'id'
            return (
              <div key={key} className={cn('min-w-0', isId && 'col-span-full')}>
                <div className="text-9 font-semibold uppercase tracking-[0.06em] text-ink-4">
                  {key}
                </div>
                <div
                  title={isId ? value : undefined}
                  className={cn(
                    'text-11 text-ink-2',
                    isId ? 'truncate whitespace-nowrap font-mono' : 'break-words'
                  )}
                >
                  {value}
                </div>
              </div>
            )
          })}
        </div>
      </CollapsibleContent>
    </div>
  )
}
